import React from 'react';
import { Button, Tag } from 'antd';
import { useDispatch } from 'react-redux';
import { api } from '../../../redux/slices/api';
import { clearAuthToken } from '../../../redux/slices/user';
import { REVIEW_PHASE_LABEL, type ReviewPhase } from './reviewPhase';

interface EmbedHeaderProps {
	title: string;
	subtitle?: string | null;
	phase?: ReviewPhase;
	/** When set, shows a mode tag instead of a review phase. */
	mode?: 'programming';
	/** Rendered as a second row, so it stays pinned along with the title. */
	children?: React.ReactNode;
}

/**
 * Says what is on screen and offers the way out of the current session.
 * Embedding hosts show no chrome of their own, so this is the only place a
 * reviewer can see either.
 */
const EmbedHeader: React.FC<EmbedHeaderProps> = ({
	title,
	subtitle,
	phase,
	mode,
	children,
}) => {
	const dispatch = useDispatch();

	// Dropping the token is enough to leave: the app-wide auth gate sees it go
	// and sends this frame to the login screen, carrying the current path so
	// the next person lands back here.
	const signOut = () => {
		dispatch(clearAuthToken());
		// Identity decides which review phase this page opens in, so the
		// departing user's cached responses must not answer for the next one.
		dispatch(api.util.resetApiState());
	};

	return (
		<header className="embed-header">
			<div className="embed-header__top">
				<div className="embed-header__titles">
					<strong className="embed-header__title" title={title}>
						{title}
					</strong>
					{subtitle ? (
						<span className="embed-header__subtitle" title={subtitle}>
							{subtitle}
						</span>
					) : null}
				</div>
				<div className="embed-header__actions">
					{mode === 'programming' ? (
						<Tag className="embed-header__phase" color="green">
							Programming
						</Tag>
					) : null}
					{phase ? (
						<Tag
							className="embed-header__phase"
							color={phase === 'secondary_review' ? 'purple' : 'blue'}
						>
							{REVIEW_PHASE_LABEL[phase]}
						</Tag>
					) : null}
					<Button size="small" onClick={signOut}>
						Sign out
					</Button>
				</div>
			</div>
			{children}
		</header>
	);
};

export default EmbedHeader;
